import React, { useEffect, useState } from 'react';
import ViewRenderer from './views';
import styles from './styles.module.scss';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import Flex from '../../../components/Flex';
import { commonImages, operatorImages } from '../../../assets/images';
import { API } from '../../../requests';
import PopupContainer from '../../../components/PopupContainer';
import Popup from '../../../components/SimplePopup';
import POPUP_CONSTANTS from '../../../constants/popup-constants';
import strings from '../../../globalization';
import { useLocation, useHistory } from 'react-router-dom';

const RefundManagementComponent = () => {
    const location = useLocation();
    const [orderId, setOrderId] = useState(location.state?.orderId ? location.state?.orderId : '');
    const [orderIdError, setOrderIdError] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [refundData, setRefundData] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [isAddingCredits, setIsAddingCredits] = useState(false);
    const [popup, setPopup] = useState(null);

    console.log(location.state);
    useEffect(() => {
        // Auto-search if orderId exists from navigation
        if (orderId && location.state?.orderId) {
            handleSearch();
        }
    }, []);
    // Format amount with currency symbol
    const formatAmount = (amount) => {
        if (amount) {
            return `₹${parseFloat(amount).toFixed(2)}`;
        }
        return 'N/A';
    };

    // Format date from API response
    const formatDate = (dateString) => {
        if (dateString) {
            try {
                return new Date(dateString).toLocaleDateString('en-IN');
            } catch (error) {
                return dateString;
            }
        }
        return 'N/A';
    };

    const handleSearch = async () => {
        if (!orderId.trim()) {
            setOrderIdError('Order ID is required');
            return;
        }

        setOrderIdError('');
        setSearchError('');
        setIsSearching(true);
        setShowResults(false);

        try {
            const response = await API.getRefundByOrderId(orderId.trim());

            if (response.data) {
                setRefundData(response.data);
                setShowResults(true);
            } else {
                setSearchError('No refund data found for this Order ID.');
            }
        } catch (error) {
            console.error('Error fetching refund data:', error);

            if (error.response?.status === 404) {
                setSearchError('Order not found. Please check the Order ID and try again.');
            } else if (error.response?.status === 401) {
                setSearchError('You are not authorized to view this information.');
            } else {
                setSearchError(error.response?.data?.message || 'Failed to fetch order details. Please try again.');
            }
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddCredits = async () => {
        if (!refundData?.orderId || !refundData?.paymentId) {
            alert('Missing order information. Please search again.');
            return;
        }

        setIsAddingCredits(true);

        try {
            const requestBody = {
                orderId: refundData.orderId,
                paymentId: refundData.paymentId,
                signature: ""
            };

            const response = await API.addRefunds(requestBody);

            if (response.data) {
                showSuccessPopup('Credits added successfully');
                handleSearch(refundData.orderId);
            } else {
                alert('Failed to add credits. Please try again.');
            }
        } catch (error) {
            console.error('Error adding credits:', error);

            if (error.response?.status === 401) {
                alert('You are not authorized to perform this action.');
            } else if (error.response?.status === 400) {
                alert(error.response?.data?.message || 'Invalid request. Please check the order details.');
            } else {
                alert(error.response?.data?.message || 'Failed to add credits. Please try again.');
            }
        } finally {
            setIsAddingCredits(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !isSearching) {
            handleSearch();
        }
    };

    const getStatusBadgeClass = (status, type) => {
        const baseClass = styles.statusBadge;

        if (type === 'sanya') {
            switch (status?.toLowerCase()) {
                case 'payment_success':
                case 'approved':
                    return `${baseClass} ${styles.statusApproved}`;
                case 'payment_pending':
                case 'pending':
                    return `${baseClass} ${styles.statusPending}`;
                case 'payment_failed':
                case 'rejected':
                case 'failed':
                    return `${baseClass} ${styles.statusRejected}`;
                default:
                    return `${baseClass} ${styles.statusDefault}`;
            }
        } else if (type === 'razorpay') {
            switch (status?.toLowerCase()) {
                case 'captured':
                case 'success':
                    return `${baseClass} ${styles.statusCaptured}`;
                case 'fail':
                case 'failed':
                    return `${baseClass} ${styles.statusFailed}`;
                case 'pending':
                    return `${baseClass} ${styles.statusPending}`;
                default:
                    return `${baseClass} ${styles.statusDefault}`;
            }
        }

        return `${baseClass} ${styles.statusDefault}`;
    };

    const formatStatusText = (status) => {
        return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase();
    };

    // Popup helper functions
    const closePopup = () => {
        setPopup(null);
    };


    const showSuccessPopup = (successMessage) => {
        setPopup({
            type: POPUP_CONSTANTS.SIMPLE_POPUP,
            message: successMessage,
            onClose: closePopup,
            headingImage: operatorImages.successIcon,
            buttons: [
                {
                    text: strings.okayPopup || 'OK',
                    onClick: closePopup,
                    outline: true,
                },
            ],
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {/* Header */}
                <div className={styles.header}>
                    <h1 className={styles.title}>Refund Management</h1>
                </div>

                {/* Search Form */}
                <div className={styles.searchSection}>
                    <div className={styles.formCard}>
                        <div className={styles.inputContainer}>
                            <CustomInput
                                inputLable="Order ID"
                                placeholder="Enter Order ID"
                                value={orderId}
                                onTextChange={setOrderId}
                                onKeyDown={handleKeyDown}
                                error={orderIdError}
                                required={true}
                            />
                        </div>
                        <div className={styles.buttonContainer}>
                            <Button
                                label={
                                    <Flex direction="row" className={styles.buttonContent}>
                                        {!isSearching && (
                                            <img
                                                src={commonImages.searchIcon}
                                                alt="Search"
                                                className={styles.buttonIcon}
                                            />
                                        )}
                                        <span>{isSearching ? 'Searching...' : 'Search'}</span>
                                    </Flex>
                                }
                                buttonStyle="primary"
                                onClick={handleSearch}
                                disabled={isSearching}
                                className={styles.searchButton}
                            />
                        </div>
                    </div>
                </div>

                {/* Error Section */}
                {searchError && (
                    <div className={styles.errorSection}>
                        <div className={styles.errorCard}>
                            <div className={styles.errorIcon}>⚠️</div>
                            <div className={styles.errorMessage}>{searchError}</div>
                        </div>
                    </div>
                )}

                {/* Results Section */}
                {showResults && refundData && (
                    <div className={styles.resultsSection}>
                        <div className={styles.refundCard}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Transaction Details</h2>
                            </div>

                            <div className={styles.cardContent}>
                                <div className={styles.detailsGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Order ID:</span>
                                        <span className={styles.detailValue}>{refundData.orderId || 'N/A'}</span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Order Date:</span>
                                        <span className={styles.detailValue}>{formatDate(refundData.createdDate)}</span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Payment ID:</span>
                                        <span className={styles.detailValue}>{refundData.paymentId || 'N/A'}</span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Amount:</span>
                                        <span className={styles.detailValue}>{formatAmount(refundData.amount)}</span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Payment Reference ID:</span>
                                        <span className={styles.detailValue}>{refundData.referenceId || 'N/A'}</span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Sanya Application Status:</span>
                                        <span className={getStatusBadgeClass(refundData.status, 'sanya')}>
                                            {formatStatusText(refundData.status)}
                                        </span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Razorpay Status:</span>
                                        <span className={getStatusBadgeClass(refundData.razorpayPaymentStatus, 'razorpay')}>
                                            {formatStatusText(refundData.razorpayPaymentStatus)}
                                        </span>
                                    </div>

                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Agency Name:</span>
                                        <span className={styles.detailValue}>{refundData.companyName || 'N/A'}</span>
                                    </div>
                                </div>

                                <div className={styles.cardActions}>
                                    <Button
                                        label={isAddingCredits ? 'Adding Credits...' : 'Add Credits'}
                                        buttonStyle="primary"
                                        onClick={handleAddCredits}
                                        disabled={
                                            isAddingCredits ||
                                            !(refundData.razorpayPaymentStatus?.toLowerCase() === 'success' &&
                                                refundData.status?.toLowerCase() !== 'payment_completed')
                                        }
                                        className={styles.addCreditsButton}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Popup Container */}
            {popup && (
                <PopupContainer>
                    <Popup {...popup} />
                </PopupContainer>
            )}
        </div>
    );
};

const RefundManagement = (props) => {
    return <ViewRenderer {...props} />;
};

export { RefundManagementComponent };
export default RefundManagement;
